// Fernet token generation using the browser's native Web Crypto API.
// Fernet spec: version(1) + timestamp(8) + iv(16) + ciphertext + hmac(32), URL-safe base64.
async function buildAuthToken() {
  const keyStr   = process.env.REACT_APP_FERNET_KEY.replace(/-/g, '+').replace(/_/g, '/');
  const keyBytes = Uint8Array.from(atob(keyStr), c => c.charCodeAt(0));

  const signingKey    = keyBytes.slice(0, 16);
  const encryptionKey = keyBytes.slice(16, 32);

  const msgBytes = new TextEncoder().encode(
    JSON.stringify({ user: 'poolpix-ui', app: 'catalogue-proposal' })
  );

  const iv = window.crypto.getRandomValues(new Uint8Array(16));

  const ts = new Uint8Array(8);
  let t = Math.floor(Date.now() / 1000);
  for (let i = 7; i >= 0; i--) { ts[i] = t & 0xff; t = Math.floor(t / 256); }

  const encKey     = await window.crypto.subtle.importKey('raw', encryptionKey, { name: 'AES-CBC' }, false, ['encrypt']);
  const ciphertext = new Uint8Array(await window.crypto.subtle.encrypt({ name: 'AES-CBC', iv }, encKey, msgBytes));

  const preHmac = new Uint8Array([0x80, ...ts, ...iv, ...ciphertext]);

  const hmacKey = await window.crypto.subtle.importKey('raw', signingKey, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const hmac    = new Uint8Array(await window.crypto.subtle.sign('HMAC', hmacKey, preHmac));

  const token = new Uint8Array([...preHmac, ...hmac]);
  return btoa(String.fromCharCode(...token)).replace(/\+/g, '-').replace(/\//g, '_');
}

export function decodeProposalToken(token) {
  const decoded = atob(token.replace(/-/g, '+').replace(/_/g, '/'));
  const [projectId, productId] = decoded.split('_');
  return { projectId, productId };
}

export async function fetchProposalResults(projectId, productId) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/project-results/lookup?product_id=${productId}`,
    { headers: { 'X-Auth-Token': token } }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

// Resolve a short-link product id / custom alias (e.g. "12" or "NOB-12") to its proposal URL.
// Returns the first sample_page_link, or null when the id can't be resolved (404 / no links).
export async function resolveProductLink(id) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/product-custom-aliases/link?id=${encodeURIComponent(id)}`,
    { headers: { 'X-Auth-Token': token } }
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const data = await res.json().catch(() => ({}));
  // Live API returns `sample_page_links: [url, ...]` (strings); spec form is
  // `sample_links: [{ sample_page_link }]`. Support both.
  const first = data?.sample_page_links?.[0];
  return (typeof first === 'string' ? first : first?.sample_page_link)
      || data?.sample_links?.[0]?.sample_page_link
      || null;
}

// Trigger closeup generation for one or more proposal images.
// items: [{ product_id, combination, image_id }]  element_category: deck_coping | water_waterline | spa_bond_beam
export async function createCloseups({ element_category, items }) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/queue/closeups`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ element_category, items }),
    }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json().catch(() => ({}));
}

// Set retry_status / closeup_status on a project-result row (by unique_id).
// fields e.g. { retry_status: 'in_progress' } or { closeup_status: 'in_progress' }.
// Marked 'in_progress' on click; the worker sets it 'completed' when the render/closeup is saved.
export async function setProjectResultStatus(unique_id, fields) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/project-results/status`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ unique_id, ...fields }),
    }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json().catch(() => ({}));
}

// Returns a dict of Component -> [Option1 material values], used by the retry picker.
export async function fetchOption1Values() {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/products/active/option1-values`,
    { headers: { 'X-Auth-Token': token } }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

// Trigger a retry. Body fields:
//   image_ids: keyed "image_ids" but VALUES are project_results.unique_id of the selected outputs.
//   products:  [{ product_id, source_element, target_elements: [...] }]
//   product_type:        present for a material-replace retry (backend searches that material).
//   use_primary_product: true for regenerating the product's OWN element with the current product
//                        (backend skips the combination search). Mutually exclusive with product_type.
export async function createProductCombinations(body) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/queue/product-combinations`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json().catch(() => ({}));
}

export async function fetchPublishStatus(productId) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/product-sample-links/?product_id=${productId}`,
    { headers: { 'X-Auth-Token': token } }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

export async function updatePublishStatus(productId, publishStatus) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/product-sample-links/${productId}/publish-status`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ publish_status: publishStatus }),
    }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

export async function updateSortIds(entries) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/project-results/sort-id`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ entries }),
    }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

export async function updateActiveStatus({ project_id, product_id, combination, image_id, active_status }) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/project-results/active-status`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ project_id, product_id, combination, image_id, active_status }),
    }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

export async function deleteProjectResult({ project_id, product_id, combination, image_id }) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/project-results/entry`,
    {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ project_id, product_id, combination, image_id }),
    }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

export async function fetchProject(projectId) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/projects/${projectId}`,
    { headers: { 'X-Auth-Token': token } }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

export async function fetchProductDetails(productId) {
  const token    = await buildAuthToken();
  const res      = await fetch(
    `${process.env.REACT_APP_API_BASE}/products/active/filter?search=${productId}`,
    { headers: { 'X-Auth-Token': token } }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  const products = await res.json();
  return products.find(p => String(p.Product_id) === String(productId)) || null;
}

export async function fetchProductById(productId) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/products/${productId}`,
    { headers: { 'X-Auth-Token': token } }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}

export async function fetchUser(userId) {
  const token = await buildAuthToken();
  const res   = await fetch(
    `${process.env.REACT_APP_API_BASE}/users/${userId}`,
    { headers: { 'X-Auth-Token': token } }
  );
  if (!res.ok) throw new Error(`API error ${res.status}`);
  return res.json();
}
